<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <h3>Samlet omsetningsrapport</h3>
    <div class="mt-2" style="font-style: italic; color:var(--bo-muted)">
      <span>Samlet omsetning på tvers av flere merchants, fordelt per kanal (POS, Kiosk, Nettbutikk).
        Velg hvilke merchants og hvilken periode rapporten skal gjelde for.</span>
    </div>

    <!-- Merchant selection -->
    <div class="mt-4" style="max-width:640px">
      <div class="d-flex align-items-center" style="gap:1rem">
        <span class="ingredient-title mb-0">Velg merchants</span>
        <span style="color:var(--bo-muted)">{{ selectedCount }} av {{ merchants.length }} valgt</span>
        <button class="btn btn-sm btn-outline-secondary ms-auto" @click="selectAll">Velg alle</button>
        <button class="btn btn-sm btn-outline-secondary" @click="clearAll">Fjern alle</button>
      </div>
      <input class="form-control mt-2" type="text" v-model="merchantSearch" placeholder="Søk merchant…" />
      <div class="merchant-picker mt-2">
        <label v-for="m in filteredMerchants" :key="m.merchant" class="merchant-row">
          <input type="checkbox" :value="m.merchant" v-model="selectedMerchants" />
          <span class="merchant-name">{{ m.name }}</span>
          <code class="merchant-slug">{{ m.merchant }}</code>
        </label>
        <div v-if="merchants.length && filteredMerchants.length === 0" class="p-2" style="color:var(--bo-muted)">
          Ingen merchants samsvarer med søket.
        </div>
      </div>
    </div>

    <!-- Date range -->
    <div style="max-width:545px">
      <SelectDate ref="selectDateFrom" label="Velg fra og med dato" :selectedDate="fromDateString" />
      <SelectDate ref="selectDateTo" label="Velg til og med dato" :selectedDate="toDateString" />

      <button type="button" class="bo_btn mt-3 mb-2" :disabled="selectedCount === 0 || generating" @click="showReport">
        {{ generating ? 'Henter…' : 'Vis samlet omsetningsrapport' }}
      </button>
      <p v-if="selectedCount === 0" style="color:var(--bo-muted)">Velg minst én merchant.</p>
    </div>

    <!-- Result -->
    <div v-if="report && !generating" class="mt-4">
      <p style="color:var(--bo-muted)">Periode: {{ formatDate(report.fromDate) }} – {{ formatDate(report.toDate) }} · {{ report.merchants.length }} merchant(s)</p>

      <div v-if="!report.totals.grand.brutto" style="color:var(--bo-muted)">Ingen omsetning i perioden.</div>

      <template v-else>
        <!-- KPI cards: platform-health summary -->
        <div class="row g-3 mb-4">
          <div class="col-12 col-md-4">
            <KpiCard
              :value="grandBrutto"
              label="Samlet omsetning"
              format="currency"
              icon="coins"
              :trend="growthTrend"
              :subtitle="previousPeriodSubtitle"
            />
          </div>
          <div class="col-12 col-md-4">
            <KpiCard
              :value="activeMerchants"
              label="Aktive merchants"
              format="number"
              icon="store"
              icon-bg-color="#2e7d32"
              :subtitle="'av ' + report.merchants.length + ' valgt'"
            />
          </div>
          <div class="col-12 col-md-4">
            <KpiCard
              :value="avgPerActiveMerchant"
              label="Snitt per aktiv merchant"
              format="currency"
              icon="chart-line"
              icon-bg-color="#1565c0"
              subtitle="Brutto omsetning"
            />
          </div>
        </div>

        <!-- Trend: total turnover over time (platform health) -->
        <SalesTrendChart
          class="mb-4"
          title="Samlet omsetning over tid"
          :subtitle="activeTab === 0 ? 'Daglig brutto omsetning (kr)' : 'Månedlig brutto omsetning (kr)'"
          :labels="activeTab === 0 ? dailyLabels : monthlySeries.labels"
          :data="activeTab === 0 ? dailyData : monthlySeries.data"
          :tabs="['Daglig', 'Månedlig']"
          :activeTab="activeTab"
          :chartType="activeTab === 0 ? 'area' : 'bar'"
          :isMonthlyView="activeTab === 1"
          @tab-change="activeTab = $event"
        />

        <!-- Brutto per merchant × channel -->
        <h5 class="mt-3">Brutto omsetning per merchant og kanal</h5>
        <div class="table-responsive">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th>Merchant</th>
                <th v-for="ch in report.channels" :key="ch" class="text-end">{{ channelLabel(ch) }}</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in sortedMerchants" :key="row.merchant">
                <td>{{ merchantName(row.merchant) }}</td>
                <td v-for="ch in report.channels" :key="ch" class="text-end">{{ formatKr(row.channels[ch].brutto) }}</td>
                <td class="text-end bold">{{ formatKr(row.total.brutto) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="bold" style="border-top:2px solid var(--bo-ink)">
                <td>Total</td>
                <td v-for="ch in report.channels" :key="ch" class="text-end">{{ formatKr(report.totals[ch].brutto) }}</td>
                <td class="text-end">{{ formatKr(report.totals.grand.brutto) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Per-channel summary: brutto / netto / MVA / tips -->
        <h5 class="mt-4">Per kanal (samlet)</h5>
        <div class="table-responsive">
          <table class="table align-middle" style="max-width:640px">
            <thead>
              <tr>
                <th>Kanal</th>
                <th class="text-end">Brutto</th>
                <th class="text-end">Netto</th>
                <th class="text-end">MVA</th>
                <th class="text-end">Tips</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ch in report.channels" :key="ch">
                <td>{{ channelLabel(ch) }}</td>
                <td class="text-end">{{ formatKr(report.totals[ch].brutto) }}</td>
                <td class="text-end">{{ formatKr(report.totals[ch].netto) }}</td>
                <td class="text-end">{{ formatKr(report.totals[ch].vat) }}</td>
                <td class="text-end">{{ formatKr(report.totals[ch].tips) }}</td>
              </tr>
              <tr class="bold" style="border-top:2px solid var(--bo-ink)">
                <td>Total</td>
                <td class="text-end">{{ formatKr(report.totals.grand.brutto) }}</td>
                <td class="text-end">{{ formatKr(report.totals.grand.netto) }}</td>
                <td class="text-end">{{ formatKr(report.totals.grand.vat) }}</td>
                <td class="text-end">{{ formatKr(report.totals.grand.tips) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style="color:var(--bo-muted); font-size:0.85rem">Beløp i kr, inkl. MVA der annet ikke er angitt. MVA beregnet per varekategori fra dens egen MVA-sats.</p>
      </template>
    </div>

    <p v-if="error" class="text-danger mt-3">{{ error }}</p>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import SelectDate from "@/components/other/SelectDate";
import SalesTrendChart from "@/components/dashboard/SalesTrendChart";
import KpiCard from "@/components/dashboard/KpiCard";
import MerchantService from "@/services/merchant/MerchantService";
import SystemService from "@/services/system/SystemService";
import moment from "moment";

const CHANNEL_LABELS = { POS: "POS", KIOSK: "Kiosk", WEBSHOP: "Nettbutikk" };

export default {
  name: "AggregatedTurnoverReport",
  components: { LoadingSpinner, SelectDate, SalesTrendChart, KpiCard },
  data() {
    return {
      loading: true,
      generating: false,
      error: "",
      merchants: [],
      merchantSearch: "",
      selectedMerchants: [],
      fromDateString: moment().startOf("month").format("YYYY-MM-DD"),
      toDateString: moment().format("YYYY-MM-DD"),
      report: null,
      activeTab: 0 // 0 = daily, 1 = monthly
    };
  },
  computed: {
    // --- KPI cards ---
    grandBrutto() {
      return this.report ? Number(this.report.totals.grand.brutto) || 0 : 0;
    },
    previousBrutto() {
      return this.report ? Number(this.report.previousBrutto) || 0 : 0;
    },
    // Growth % vs the previous equal-length period (null when there's no prior turnover to compare to).
    growthTrend() {
      if (!this.report || this.previousBrutto <= 0) return null;
      return Math.round(((this.grandBrutto - this.previousBrutto) / this.previousBrutto) * 100);
    },
    activeMerchants() {
      if (!this.report) return 0;
      return this.report.merchants.filter((m) => m.total && Number(m.total.brutto) > 0).length;
    },
    avgPerActiveMerchant() {
      return this.activeMerchants ? this.grandBrutto / this.activeMerchants : 0;
    },
    previousPeriodSubtitle() {
      if (!this.report || !this.report.previousFromDate) return "";
      return "Forrige periode: " + this.formatKr0(this.previousBrutto) + " kr";
    },
    // Daily trend straight from the backend series (continuous, zero-filled).
    dailyLabels() {
      return this.report && this.report.series ? this.report.series.map((s) => s.date) : [];
    },
    dailyData() {
      return this.report && this.report.series ? this.report.series.map((s) => s.brutto) : [];
    },
    // Monthly trend — sum the daily series into calendar months.
    monthlySeries() {
      if (!this.report || !this.report.series) return { labels: [], data: [] };
      const byMonth = new Map();
      for (const point of this.report.series) {
        const key = String(point.date).slice(0, 7); // YYYY-MM
        byMonth.set(key, (byMonth.get(key) || 0) + (Number(point.brutto) || 0));
      }
      const keys = [...byMonth.keys()].sort();
      return {
        labels: keys.map((k) => moment(k, "YYYY-MM").locale("nb").format("MMM YYYY")),
        data: keys.map((k) => byMonth.get(k))
      };
    },
    filteredMerchants() {
      const q = this.merchantSearch.trim().toLowerCase();
      if (!q) return this.merchants;
      return this.merchants.filter((m) =>
        [m.name, m.merchant].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
      );
    },
    selectedCount() {
      return this.selectedMerchants.length;
    },
    sortedMerchants() {
      if (!this.report) return [];
      // Highest turnover first — most useful ordering for an aggregate report.
      return [...this.report.merchants].sort((a, b) => b.total.brutto - a.total.brutto);
    }
  },
  mounted() {
    MerchantService.listAllMerchants().then(
      (response) => {
        const list = Array.isArray(response.data) ? response.data : [];
        // Only merchants with a slug; sort by name for the picker.
        this.merchants = list
          .filter((m) => m.merchant)
          .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "nb"));
        this.selectedMerchants = this.merchants.map((m) => m.merchant); // default: all selected
        this.loading = false;
      },
      (error) => {
        console.log(error);
        this.error = "Kunne ikke hente merchants.";
        this.loading = false;
      }
    );
  },
  methods: {
    selectAll() {
      this.selectedMerchants = this.merchants.map((m) => m.merchant);
    },
    clearAll() {
      this.selectedMerchants = [];
    },
    channelLabel(ch) {
      return CHANNEL_LABELS[ch] || ch;
    },
    merchantName(slug) {
      const m = this.merchants.find((x) => x.merchant === slug);
      return m ? m.name : slug;
    },
    formatKr(num) {
      const n = Number(num) || 0;
      return n.toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatKr0(num) {
      const n = Number(num) || 0;
      return n.toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    formatDate(isoDate) {
      const d = moment(isoDate, "YYYY-MM-DD");
      return d.isValid() ? d.format("DD.MM.YYYY") : isoDate;
    },
    showReport() {
      if (this.selectedCount === 0) return;
      this.generating = true;
      this.error = "";
      this.report = null;

      const fromDate = this.$refs.selectDateFrom.getSelectedDate();
      const toDate = this.$refs.selectDateTo.getSelectedDate();

      SystemService.getAggregatedTurnoverReport(this.selectedMerchants, fromDate, toDate).then(
        (response) => {
          this.report = response.data;
          this.generating = false;
        },
        (error) => {
          console.log(error);
          this.error = "Kunne ikke hente rapporten. Prøv igjen.";
          this.generating = false;
        }
      );
    }
  }
};
</script>

<style scoped>
.ingredient-title {
  display: block;
  font-weight: bold;
  margin: 15px 0 10px 0;
}
.merchant-picker {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
}
.merchant-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.3rem 0.25rem;
  cursor: pointer;
  border-bottom: 1px solid var(--bo-surface-2);
}
.merchant-row:last-child {
  border-bottom: none;
}
.merchant-name {
  flex: 1;
}
.merchant-slug {
  color: var(--bo-muted);
  font-size: 0.85rem;
}
.bold {
  font-weight: bold;
}
</style>
